import { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { fetchGames, winningOutcomeLabel, motifDescription } from "@/features/games";
import { fetchGameAnalysis } from "@/shared/api/engine.api";
import { ChesslaBoardSurface } from "@/features/board/surface";
import { resolveBoardPreferences } from "@/features/board/review-board-settings";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { BoardEvidenceWheel } from "@/shared/components/chessboard/BoardEvidenceWheel";
import { boardEvidenceMotifAnnotation } from "@/shared/components/motif-annotation";
import { MotifBadge } from "@/shared/components/MotifBadge";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { lastMoveSquareStyles, latestAnalyzedWin, latestWinBoard, type LatestWinBoardState } from "../latest-win";

const INITIAL = latestWinBoard(null, null);

export const LatestWinBoard = () => {
  const settings = resolveBoardPreferences(useUserSettingsStore((state) => state.settings), "review");
  const [board, setBoard] = useState<LatestWinBoardState>(INITIAL);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const win = latestAnalyzedWin(await fetchGames(50));
        if (!win) return;
        const analysis = await fetchGameAnalysis(win.id);
        if (active) setBoard(latestWinBoard(win, analysis));
      } catch {
        // The resolved fallback remains illustrative.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const boardLabel = board.gameId
    ? `Your latest analyzed win against ${board.opponent}${board.lastMove ? `. Last move ${board.lastMove.san}${board.lastMove.checkmate ? ", checkmate" : ""}` : ""}`
    : "Illustrative starting position";
  const outcome = board.outcomeMotif ? boardEvidenceMotifAnnotation([board.outcomeMotif]) : null;

  return (
    <Box
      data-about-live-board="true"
      aria-label={loading ? "Loading your latest analyzed win" : boardLabel}
      aria-busy={loading}
      sx={{
        width: "100%",
        maxWidth: 560,
        justifySelf: "center",
        border: "2px solid",
        borderColor: "text.primary",
        bgcolor: "background.default",
        boxShadow: "12px 12px 0 #ff3d12",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "2px solid",
          borderColor: "text.primary",
        }}
      >
        <Typography sx={{ fontSize: ".65rem", fontWeight: 900, letterSpacing: ".07em" }}>
          {loading ? "LOADING YOUR LATEST WIN" : board.gameId ? "YOUR LATEST WIN" : "A REAL BOARD"}
        </Typography>
        {!loading && (
          <Typography sx={{ color: "secondary.main", fontSize: ".65rem", fontWeight: 900, letterSpacing: ".07em" }}>
            {board.gameId
              ? `VS ${board.opponent?.toUpperCase()} · ${board.outcomeMotif ? winningOutcomeLabel(board.outcomeMotif) : board.playedAt?.slice(0, 10)}`
              : "READY FOR YOUR GAMES"}
          </Typography>
        )}
      </Box>
      {loading ? (
        <Box sx={{ aspectRatio: "1", display: "grid", placeItems: "center" }}>
          <ChesslaLoadingMark label="Loading latest win" />
        </Box>
      ) : (
        <Box data-about-last-move={board.lastMove?.san} sx={{ position: "relative" }}>
          <ChesslaBoardSurface
            settings={settings}
            relationships={Boolean(board.gameId)}
            id="about-latest-win"
            position={board.fen}
            orientation={board.orientation}
            allowDragging={false}
            allowDrawingArrows={false}
            squareStyles={lastMoveSquareStyles(board.lastMove)}
            evidence={(relationships) => (
              <BoardEvidenceWheel
                displayFen={board.fen}
                orientation={board.orientation}
                items={[
                  ...relationships,
                  ...(board.lastMove && outcome
                    ? [
                        {
                          id: "game-outcome",
                          kind: "motif" as const,
                          square: board.lastMove.to,
                          label: outcome.title,
                          color: outcome.color,
                          description: motifDescription(board.outcomeMotif!),
                          glyph: <MotifBadge kind={outcome.kind} label={outcome.label} color={outcome.color} />,
                          disclosure: (
                            <>
                              <Typography variant="subtitle1">{outcome.title}</Typography>
                              <Typography variant="body2">{motifDescription(board.outcomeMotif!)}</Typography>
                            </>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          />
        </Box>
      )}
      {!loading && (
        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderTop: "2px solid",
            borderColor: "text.primary",
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ color: "secondary.main", fontSize: ".65rem", fontWeight: 900, letterSpacing: ".1em" }}>
              {board.gameId ? "RECORDED POSITION" : "ILLUSTRATIVE POSITION"}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: { xs: "1.15rem", md: "1.55rem" },
                fontWeight: 950,
                lineHeight: 0.95,
                letterSpacing: "-.045em",
              }}
            >
              {board.gameId ? "THE BOARD REMEMBERS." : "YOUR EVIDENCE LANDS HERE."}
            </Typography>
          </Box>
          {board.gameId && (
            <Link
              component={RouterLink}
              to={`/games/${encodeURIComponent(board.gameId)}`}
              sx={{
                flexShrink: 0,
                color: "text.primary",
                fontSize: ".68rem",
                fontWeight: 900,
                letterSpacing: ".07em",
                textDecorationColor: "secondary.main",
                textUnderlineOffset: 4,
              }}
            >
              REVIEW ↗
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};
